import React, { useState, useEffect, useCallback } from 'react';
import './AdminSitePages.css';
import { fetchWithAuth } from '../utils/auth';
import { API_URL } from '../config';

// Turns one page's `content` object into an editable field list, remembering whether each
// value was a list (newline-joined for editing) or plain text, so saving can convert it back
// to the right shape. A brand-new field defaults to 'text' until the admin marks it 'list'.
const contentToFields = (content) => Object.entries(content || {}).map(([key, value]) => ({
  key,
  type: Array.isArray(value) ? 'list' : 'text',
  value: Array.isArray(value) ? value.join('\n') : (value ?? ''),
}));

const fieldsToContent = (fields) => {
  const content = {};
  for (const f of fields) {
    if (!f.key.trim()) continue;
    content[f.key.trim()] = f.type === 'list'
      ? f.value.split('\n').map(s => s.trim()).filter(Boolean)
      : f.value;
  }
  return content;
};

const AdminSitePages = () => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [editingKey, setEditingKey] = useState(null);
  const [editNavLabel, setEditNavLabel] = useState('');
  const [editFields, setEditFields] = useState([]);
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchWithAuth(`${API_URL}/api/admin/site-pages`);
      const data = await res.json();
      if (res.ok) setPages(data.pages || []);
      else setError(data.error || 'Failed to load site pages.');
    } catch {
      setError('Network error.');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openEdit = (page) => {
    setEditingKey(page.page_key);
    setEditNavLabel(page.nav_label);
    setEditFields(contentToFields(page.content));
    setMessage('');
    setError('');
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setEditFields([]);
  };

  const addField = () => {
    const key = window.prompt('New field name (e.g. "venue" or "registration_bullets"):');
    if (!key || !key.trim()) return;
    if (editFields.some(f => f.key === key.trim())) {
      setError('A field with that name already exists.');
      return;
    }
    const isList = window.confirm('Is this a list of items (one per line), like a bullet list? Cancel = plain text field.');
    setEditFields(prev => [...prev, { key: key.trim(), type: isList ? 'list' : 'text', value: '' }]);
  };

  const removeField = (key) => {
    setEditFields(prev => prev.filter(f => f.key !== key));
  };

  const updateFieldValue = (key, value) => {
    setEditFields(prev => prev.map(f => f.key === key ? { ...f, value } : f));
  };

  const saveEdit = async () => {
    setSaving(true);
    setError('');
    try {
      const res = await fetchWithAuth(`${API_URL}/api/admin/site-pages/${editingKey}`, {
        method: 'PUT',
        body: JSON.stringify({
          enabled: pages.find(p => p.page_key === editingKey)?.enabled ?? false,
          nav_label: editNavLabel,
          content: fieldsToContent(editFields),
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        await load();
        cancelEdit();
        setMessage('Saved.');
      } else {
        setError(data.error || 'Failed to save.');
      }
    } catch {
      setError('Network error.');
    }
    setSaving(false);
  };

  const toggleEnabled = async (page) => {
    setToggling(page.page_key);
    try {
      const res = await fetchWithAuth(`${API_URL}/api/admin/site-pages/${page.page_key}`, {
        method: 'PUT',
        body: JSON.stringify({ enabled: !page.enabled, nav_label: page.nav_label, content: page.content }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setPages(prev => prev.map(p => p.page_key === page.page_key ? { ...p, enabled: data.enabled } : p));
      } else {
        setError(data.error || 'Failed to update.');
      }
    } catch {
      setError('Network error.');
    }
    setToggling(null);
  };

  return (
    <div className="site-pages-container">
      <div className="site-pages-header">
        <h2>Seasonal Site Pages</h2>
        <p className="site-pages-subtitle">
          Turn seasonal public pages (Vasantha Vizha, Join Game, ...) on or off, and edit their content
          without a deploy. Calendar, FAQ, Contact Us, and Location are managed elsewhere and always shown.
        </p>
      </div>

      {error && <div className="error-banner">{error}</div>}
      {message && <p className="site-pages-success-msg">{message}</p>}

      {loading ? (
        <p style={{ padding: '20px', color: '#64748b' }}>Loading…</p>
      ) : (
        <table className="admin-table site-pages-table">
          <thead>
            <tr>
              <th>Page</th>
              <th style={{ width: '110px', textAlign: 'center' }}>Status</th>
              <th style={{ width: '120px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pages.map(page => (
              <React.Fragment key={page.page_key}>
                <tr>
                  <td>
                    <strong>{page.nav_label}</strong>
                    <div className="muted">{page.page_key}</div>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <button
                      className={`site-page-toggle ${page.enabled ? 'on' : 'off'}`}
                      onClick={() => toggleEnabled(page)}
                      disabled={toggling === page.page_key}
                    >
                      {page.enabled ? 'Live' : 'Hidden'}
                    </button>
                  </td>
                  <td>
                    <button className="btn-sm" onClick={() => openEdit(page)}>Edit</button>
                  </td>
                </tr>
                {editingKey === page.page_key && (
                  <tr>
                    <td colSpan={3}>
                      <div className="site-page-edit-form">
                        <div className="form-row">
                          <label>Nav label</label>
                          <input type="text" value={editNavLabel} onChange={e => setEditNavLabel(e.target.value)} />
                        </div>

                        {editFields.map(f => (
                          <div className="form-row" key={f.key}>
                            <label>
                              {f.key} {f.type === 'list' && <span className="muted">(one item per line)</span>}
                            </label>
                            <div className="field-with-remove">
                              <textarea
                                rows={f.type === 'list' ? 5 : 2}
                                value={f.value}
                                onChange={e => updateFieldValue(f.key, e.target.value)}
                              />
                              <button className="btn-sm btn-danger" onClick={() => removeField(f.key)}>Remove</button>
                            </div>
                          </div>
                        ))}

                        <button className="btn-secondary" type="button" onClick={addField}>+ Add field</button>

                        <div className="form-actions">
                          <button className="btn-primary" onClick={saveEdit} disabled={saving}>
                            {saving ? 'Saving…' : 'Save'}
                          </button>
                          <button className="btn-secondary" onClick={cancelEdit} disabled={saving}>Cancel</button>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default AdminSitePages;
